import { useState, useEffect } from "react";
import { Users } from "lucide-react";

function CharactersVoiceActors({ animeId }) {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!animeId) return;

    const fetchCharacters = async () => {
      try {
        setLoading(true);
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const response = await fetch(`${API_BASE}/anime/${animeId}/characters`);
        
        if (!response.ok) {
          throw new Error("Failed to fetch characters");
        }
        
        const data = await response.json();
        setCharacters(data);
      } catch (err) {
        console.error("Error fetching characters:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCharacters();
  }, [animeId]);

  if (loading) {
    return (
      <div className="rounded-2xl sm:rounded-3xl border border-white/12 bg-white/7 backdrop-blur-xl p-4 sm:p-6 shadow-[0_25px_90px_rgba(0,0,0,0.45)]">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-emerald-200" />
          <h3 className="text-lg sm:text-xl font-bold text-white/90">
            Characters & Voice Actors
          </h3>
        </div>
        <p className="text-sm text-white/50">Loading characters...</p>
      </div>
    );
  }

  if (error || characters.length === 0) {
    return null; // Don't show the section if there's an error or no characters
  }

  return (
    <div className="rounded-2xl sm:rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-emerald-400/5 to-white/5 backdrop-blur-xl p-4 sm:p-5 shadow-[0_25px_80px_rgba(16,185,129,0.12)]">
      <div className="flex items-center gap-2 mb-4 sm:mb-5 pb-2.5 border-b border-white/10">
        <Users className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-300" />
        <h3 className="text-lg sm:text-xl font-bold text-white/95">
          Characters & Voice Actors
        </h3>
      </div>

      <div className="space-y-2.5 sm:space-y-3">
        {characters.map((character) => {
          const voiceActor = character.voiceActors && character.voiceActors.length > 0 
            ? character.voiceActors[0] 
            : null;

          return (
            <div
              key={character.characterId}
              className="grid grid-cols-[auto_1fr_auto] gap-2 sm:gap-3 items-center p-2 sm:p-3 rounded-xl border border-white/8 bg-white/3 hover:bg-white/5 transition"
            >
              {/* Character Section */}
              <div className="flex items-center gap-2 sm:gap-2.5">
                <div className="relative flex-shrink-0">
                  <img
                    src={character.image || "/images/placeholder-character.jpg"}
                    alt={character.name}
                    className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg object-cover border border-white/10"
                    onError={(e) => {
                      e.target.src = "/images/placeholder-character.jpg";
                    }}
                  />
                </div>
                <div className="min-w-0 overflow-hidden">
                  <p className="text-xs sm:text-base font-semibold text-white/90 truncate">
                    {character.name}
                  </p>
                  <span
                    className={`inline-block mt-0.5 sm:mt-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-xs font-medium ${
                      character.role === "Main"
                        ? "bg-emerald-500/15 border border-emerald-400/30 text-emerald-200"
                        : "bg-sky-500/15 border border-sky-400/30 text-sky-200"
                    }`}
                  >
                    {character.role}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block h-px bg-white/10" />
              <div className="sm:hidden w-px h-8 bg-white/10 justify-self-center" />

              {/* Voice Actor Section */}
              {voiceActor ? (
                <div className="flex items-center gap-2 sm:gap-2.5 justify-end">
                  <div className="min-w-0 overflow-hidden text-right">
                    <p className="text-xs sm:text-base font-semibold text-white/90 truncate">
                      {voiceActor.name}
                    </p>
                    <span className="inline-block mt-0.5 sm:mt-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-xs font-medium bg-white/10 border border-white/15 text-white/70">
                      {voiceActor.language}
                    </span>
                  </div>
                  <div className="relative flex-shrink-0">
                    <img
                      src={voiceActor.image || "/images/placeholder-va.jpg"}
                      alt={voiceActor.name}
                      className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg object-cover border border-white/10"
                      onError={(e) => {
                        e.target.src = "/images/placeholder-va.jpg";
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <p className="text-[10px] sm:text-sm text-white/40 italic">No VA data</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* More Characters Link (Placeholder) */}
      {characters.length >= 6 && (
        <div className="mt-4 sm:mt-6 text-center">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-400/25 bg-emerald-400/10 hover:bg-emerald-400/15 text-sm font-semibold text-emerald-200 transition"
            onClick={() => {
              // TODO: Implement full character list view
              console.log("View more characters");
            }}
          >
            <Users size={16} />
            View More Characters
          </button>
        </div>
      )}
    </div>
  );
}

export default CharactersVoiceActors;
